/**
 * Kalib AI — Verify your email (Screen 2 of the revised setup flow).
 *
 * Spec rules:
 *   Headline: "Verify your email"
 *   Body: "We've sent a code to your email."
 *   Action: 6-digit code field.
 *   Resend visible after 30 seconds.
 *   Buttons: Back (returns to /signup with values preserved).
 *   User cannot proceed past this until verified.
 *
 * Auto-skip if the user is already verified (admins, returning users).
 */
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BetaBadge } from "@/components/BetaBadge";
import { KalibLogo } from "@/components/KalibLogo";

const RESEND_COOLDOWN = 30; // seconds — matches backend rate-limit

export default function VerifyEmailPage() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") || "/intake";

  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_COOLDOWN);
  const inputRef = useRef(null);

  // Auto-route admins / pre-verified users straight through.
  useEffect(() => {
    if (user === null) {
      navigate("/signup");
      return;
    }
    if (user && user.email_verified) {
      navigate(next);
    }
  }, [user, navigate, next]);

  // 30s resend countdown — restarts after each successful resend.
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  // Auto-focus the code input on mount.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleCodeChange = (raw) => {
    // Only digits, max 6, no spaces. Auto-submit when 6 reached.
    const digits = (raw || "").replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    setError("");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6 || verifying) return;
    setVerifying(true);
    setError("");
    try {
      await API.post("/auth/verify_email", { code });
      updateUser({ email_verified: true });
      // Clear signup draft now that verification succeeded.
      try {
        sessionStorage.removeItem("kalib_signup_draft_v1");
      } catch {
        /* ignore */
      }
      navigate(next);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (secondsLeft > 0 || resending) return;
    setResending(true);
    setError("");
    setInfo("");
    try {
      await API.post("/auth/resend_verification_code");
      setInfo("New code sent. Check your inbox.");
      setSecondsLeft(RESEND_COOLDOWN);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setResending(false);
    }
  };

  const handleBack = async () => {
    // Spec: Back returns to /signup with values preserved.
    // Sign the user out first so they land on a clean signup form (otherwise
    // /signup would bounce them straight here again via the unverified gate).
    await logout();
    navigate("/signup");
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex flex-col" data-testid="verify-email-page">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="block"
              data-testid="verify-logo-link"
              aria-label="Kalib AI home"
            >
              <KalibLogo variant="light" height={28} testId="verify-header-logo" />
            </Link>
            <BetaBadge />
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <h1
            className="text-2xl sm:text-3xl font-medium tracking-tight text-zinc-950 font-heading mb-2"
            data-testid="verify-headline"
          >
            Verify your email
          </h1>
          <p className="text-sm text-zinc-900 mb-6" data-testid="verify-subhead">
            We&rsquo;ve sent a 6-digit code to{" "}
            <span className="font-medium text-zinc-950" data-testid="verify-email-display">
              {user?.email || "your email"}
            </span>
            .
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="verify-form">
            {error && (
              <div
                className="text-sm text-red-600 bg-red-50 border border-red-200 px-4 py-3"
                data-testid="verify-error"
              >
                {error}
              </div>
            )}
            {info && !error && (
              <div
                className="text-sm text-zinc-900 bg-amber-50 border border-amber-200 px-4 py-3"
                data-testid="verify-info"
              >
                {info}
              </div>
            )}

            <div>
              <label className="text-xs font-medium uppercase tracking-widest text-zinc-900 mb-1.5 block">
                6-digit code
              </label>
              <Input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="000000"
                maxLength={6}
                autoComplete="one-time-code"
                className="rounded-none border-zinc-200 focus:ring-amber-700 focus:border-amber-700 text-center text-lg font-mono tracking-[0.4em]"
                data-testid="verify-code-input"
              />
            </div>

            <Button
              type="submit"
              disabled={code.length !== 6 || verifying}
              className="w-full bg-amber-700 text-white hover:bg-amber-800 rounded-none text-sm font-medium h-10 disabled:opacity-40"
              data-testid="verify-submit-btn"
            >
              {verifying ? "Verifying..." : "Verify"}
            </Button>
          </form>

          <div className="mt-6 flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={handleBack}
              className="text-zinc-700 hover:text-zinc-950 transition-colors"
              data-testid="verify-back-btn"
            >
              ← Back
            </button>
            {secondsLeft > 0 ? (
              <span className="text-zinc-500" data-testid="verify-resend-cooldown">
                Resend in {secondsLeft}s
              </span>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="text-amber-700 hover:text-amber-800 font-medium transition-colors disabled:opacity-50"
                data-testid="verify-resend-btn"
              >
                {resending ? "Sending..." : "Resend code"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
